/**
 * Uferzone AR: shared contracts between modules.
 *
 * Owned by the orchestrator. Module agents implement against these types and
 * must not change them unilaterally; propose changes in their report instead.
 *
 * ─── Coordinate frames (binding) ────────────────────────────────────────────
 *
 * WGS84        lat/lon in degrees. GPS fixes and lake data files use it.
 *              Data files store coordinates as [lon, lat] (GeoJSON order).
 *
 * ENU          Local tangent plane (via ECEF) at the origin of a LocalFrame.
 *              e = metres east, n = metres north. One frame per loaded lake
 *              (origin = lake bbox centre, fixed while that lake is active),
 *              so every module shares the same frame without recentering.
 *
 * WorldENU     three.js world frame for geodata:
 *              X = east, Y = up, Z = -north (right-handed, Y up).
 *              The water surface is Y = 0. ENU (e, n) maps to (e, 0, -n).
 *
 * XR           WebXR 'local' reference space: Y up (gravity), arbitrary yaw
 *              and origin. Only exists in mode 'xr'.
 *
 * Alignment    XR <- WorldENU is  M = T(tx, waterY, tz) * R_y(yawRad).
 *              In mode 'xr' the zone group's matrix is M and the camera is
 *              driven by WebXR. In mode 'sim' M is identity and
 *              the camera pose is given directly in WorldENU.
 *
 * Headings     Degrees clockwise from TRUE north, in [0, 360).
 *              "Camera forward" = viewing direction of the rear camera
 *              (-Z of the XR viewer / of the device screen normal).
 *              In XR: thetaXr = atan2(f.x, -f.z) for forward vector f.
 *              Then yawRad = rad(thetaTrue - thetaXr)  (pinned by unit test).
 *              t = cXr - R_y(yawRad) * (E, h, -N), with h = eye height.
 *
 * Sensors      AbsoluteOrientationSensor / deviceorientationabsolute give a
 *              device -> Earth rotation with Earth X = east, Y = MAGNETIC
 *              north, Z = up. To WorldENU: q_world = R_x(-90deg) * q_sensor
 *              (north (0,1,0) -> (0,0,-1), up (0,0,1) -> (0,1,0)). The device
 *              camera looks along device -Z, same as a three.js camera.
 *              True heading = magnetic heading + declination (default +3.6).
 *
 * Position     Always from the GPS Kalman filter (all modes). In 'xr' WebXR
 *              supplies orientation and height only; t is recomputed each
 *              frame from the Kalman position (robust if ARCore locks onto
 *              the boat deck while the boat moves).
 *
 * Orientation  Chrome locks the screen orientation for the whole immersive-ar
 *   lock       session. The HUD counter-rotates (like a camera app) based on
 *              the physical device roll derived from the XR viewer pose.
 *
 * Quaternions  [x, y, z, w] tuples.
 * Time         Milliseconds, performance.now() timebase unless noted.
 */

// ─── Basic geo types ─────────────────────────────────────────────────────────

export interface LatLon {
  lat: number;
  lon: number;
}

export interface ENU {
  e: number;
  n: number;
}

/** Distance (m) and bearing (deg true, clockwise from north) from the user to a target point. */
export interface Vector2D {
  distance: number;
  bearing: number;
}

export interface GeoFix {
  lat: number;
  lon: number;
  /** 68 % horizontal accuracy radius in metres (Geolocation API semantics). */
  accuracy: number;
  /** Course over ground, deg true, if the platform provides it and speed > 0. */
  course: number | null;
  /** Speed over ground m/s, if provided. */
  speed: number | null;
  /** performance.now() timebase. */
  time: number;
  source: 'gps' | 'mock';
}

export interface LocalFrame {
  readonly origin: LatLon;
  toENU(p: LatLon): ENU;
  toLatLon(p: ENU): LatLon;
  /**
   * Angle (deg) to add to a true-north heading measured at p to express it
   * relative to the frame's +N axis (meridian convergence between p and origin).
   */
  headingCorrectionDeg(p: LatLon): number;
}

// ─── Lake data (static files under public/data) ─────────────────────────────
//
// Only OFFICIAL data is used: Kanton Luzern, dataset UFERZONE_DS_V1
// (innere / äussere / erweiterte Uferzone). No computed buffers anywhere.
// Outside the official coverage the app shows no zones ('nodata').

/** [lon, lat] */
export type Position = [number, number];
/** Closed ring (first == last). Exterior rings CCW, holes CW (GeoJSON RFC 7946). */
export type Ring = Position[];
export type PolygonCoords = Ring[];
export type MultiPolygonCoords = PolygonCoords[];
/** Open polyline. */
export type LineCoords = Position[];

export interface LakeIndexEntry {
  id: string;
  name: string;
  /** [minLon, minLat, maxLon, maxLat] of the lake outline, expanded by 1 km. */
  bbox: [number, number, number, number];
  /** Path relative to the data base URL, e.g. "lakes/vierwaldstaettersee.json". */
  file: string;
  bytes: number;
}

export interface LakeIndex {
  version: number;
  generated: string;
  /** e.g. "Kanton Luzern, UFERZONE_DS_V1, Stand 15.04.2026" */
  source: string;
  attribution: string;
  lakes: LakeIndexEntry[];
}

export interface Slipway {
  osmId: number;
  lon: number;
  lat: number;
  name: string | null;
}

export interface LakeFile {
  meta: {
    id: string;
    name: string;
    source: string;
    attribution: string;
    /** ISO date of the official data revision. */
    dataDate: string;
  };
  /** Official inner shore zone (UFERZONE = 2). */
  inner: MultiPolygonCoords;
  /** Official outer shore zone (UFERZONE = 1). */
  outer: MultiPolygonCoords;
  /** Official extended shore zone (UFERZONE = 3). May overlap others. Empty if none. */
  extended: MultiPolygonCoords;
  /** BEMERKUNG of the extended zone, e.g. "Tempolimit". */
  extendedNote: string | null;
  /**
   * Boundary of the extended zone on open water (excluding shoreline and canton
   * cut edges). Rendered as a checkered black/white "finish line".
   */
  extendedLines: LineCoords[];
  /**
   * Remaining extended-zone edges on the water that are NOT a finish line: outlines of piers,
   * jetties, harbour notches and holes. Drawn as thin lines only.
   */
  pierLines: LineCoords[];
  /** OSM leisure=slipway nodes within the lake bbox (© OpenStreetMap contributors, ODbL). */
  slipways: Slipway[];
  /**
   * OSM man_made=pier features within 150 m of the lake (© OpenStreetMap contributors, ODbL):
   * ways as polylines (closed ways/areas as closed rings), drawn as thin lines like pierLines.
   */
  osmPierLines: LineCoords[];
  /** OSM man_made=pier nodes (point-only piers), drawn as small grey markers. */
  osmPierPoints: Slipway[];
  /** Lake water area where the official data is authoritative (lake ∩ Kanton Luzern). */
  coverage: MultiPolygonCoords;
  /** Full lake outline (swissTLM3D), only to tell 'land' from 'nodata'. Never used for zones. */
  lakeOutline: MultiPolygonCoords;
  /** Real shoreline within the coverage (landward edge of the zones); for "Ufer X m". */
  shoreLines: LineCoords[];
  /** Lakeward limit of the outer zone within the coverage; for "Hinaus X m". */
  limitLines: LineCoords[];
}

// ─── Lake geometry in ENU (runtime) ─────────────────────────────────────────

/** Flattened ring/line in ENU metres: [e0, n0, e1, n1, ...]. Rings are closed. */
export type RingENU = Float64Array;
export type LineENU = Float64Array;
/** First ring exterior, rest holes. */
export type PolygonENU = RingENU[];
export type MultiPolygonENU = PolygonENU[];

export interface LakeENU {
  meta: LakeFile['meta'];
  frame: LocalFrame;
  inner: MultiPolygonENU;
  outer: MultiPolygonENU;
  extended: MultiPolygonENU;
  extendedNote: string | null;
  extendedLines: LineENU[];
  pierLines: LineENU[];
  slipways: { e: number; n: number; name: string | null; osmId: number }[];
  osmPierLines: LineENU[];
  osmPierPoints: { e: number; n: number; name: string | null; osmId: number }[];
  coverage: MultiPolygonENU;
  lakeOutline: MultiPolygonENU;
  shoreLines: LineENU[];
  limitLines: LineENU[];
}

/**
 * inner/outer: inside that official zone. open: covered lake water outside
 * inner and outer. land: outside the lake outline. nodata: on the lake but
 * outside the official coverage (other canton).
 */
export type ZoneKind = 'inner' | 'outer' | 'open' | 'land' | 'nodata';

export interface ZoneStatus {
  lakeId: string;
  lakeName: string;
  zone: ZoneKind;
  /** User is inside an extended zone (in addition to `zone`). */
  extended: boolean;
  extendedNote: string | null;
  /** 10 (km/h) inside inner, outer or extended zone (BSV), else null. */
  speedLimitKmh: number | null;
  /** Nearest shoreline point, null if none within the data. */
  shore: Vector2D | null;
  /**
   * Nearest point on the outer-zone limit line. If zone is inner/outer this is
   * "how far out to go"; if zone is open it is "distance until the shore zone
   * starts". null if not available.
   */
  limit: Vector2D | null;
}

// ─── Sensors ────────────────────────────────────────────────────────────────

export interface OrientationSample {
  /**
   * Device -> Earth rotation. Earth frame: X = east, Y = north, Z = up.
   * Device frame (W3C): X right, Y up along the screen (natural orientation),
   * Z out of the screen towards the user.
   */
  quaternion: [number, number, number, number];
  /** Camera-forward heading relative to MAGNETIC north (deg). */
  headingMagDeg: number;
  /** Camera-forward elevation above the horizon (deg, + = up). */
  pitchDeg: number;
  time: number;
  source: 'absolute-sensor' | 'deviceorientation' | 'ios-compass' | 'mock';
}

export interface PositionProvider {
  start(): Promise<void>;
  stop(): void;
  latest(): GeoFix | null;
  onFix(cb: (fix: GeoFix) => void): () => void;
  onError(cb: (err: { code: 'denied' | 'unavailable' | 'timeout'; message: string }) => void): () => void;
}

export interface OrientationProvider {
  start(): Promise<void>;
  stop(): void;
  latest(): OrientationSample | null;
  onSample(cb: (s: OrientationSample) => void): () => void;
  readonly available: boolean;
}

/** Raw viewer pose from an XRFrame in the 'local' reference space. */
export interface XrViewerSample {
  position: [number, number, number];
  orientation: [number, number, number, number];
  emulatedPosition: boolean;
  time: number;
}

// ─── Pose output (per frame) ────────────────────────────────────────────────

export type RenderMode = 'xr' | 'camera' | 'sim';
export type GpsGate = 'waiting' | 'ok' | 'degraded';
export type TrackingState = 'ok' | 'limited' | 'lost';

export interface WorldAlignment {
  yawRad: number;
  tx: number;
  waterY: number;
  tz: number;
}

export interface CameraPose {
  /** WorldENU position of the camera. */
  position: [number, number, number];
  /** WorldENU orientation of the camera (three.js camera convention: looks along -Z). */
  quaternion: [number, number, number, number];
  /** Vertical field of view in degrees (mode 'sim'). */
  vFovDeg: number;
}

export interface PoseState {
  mode: RenderMode;
  /** Filtered user position in the active frame, predicted to the current frame time. */
  userENU: ENU | null;
  userLatLon: LatLon | null;
  gpsAccuracy: number | null;
  gpsGate: GpsGate;
  /** Speed over ground in km/h (GPS Doppler speed if available, else Kalman), null if unknown. */
  speedKmh: number | null;
  /** Course over ground, deg true, null when (nearly) stationary. */
  courseDeg: number | null;
  /** Filtered camera-forward heading, deg TRUE north. */
  headingDeg: number | null;
  pitchDeg: number | null;
  tracking: TrackingState;
  /** mode 'xr' only. */
  alignment: WorldAlignment | null;
  /** mode 'sim' only. */
  camera: CameraPose | null;
}

// ─── Settings ───────────────────────────────────────────────────────────────

export type EyeHeightPreset = 'kayak' | 'seated' | 'standing' | 'yacht' | 'custom';

export const EYE_HEIGHT_PRESETS: Record<Exclude<EyeHeightPreset, 'custom'>, number> = {
  kayak: 0.7,
  seated: 1.2,
  standing: 1.8,
  yacht: 2.5,
};

export interface Settings {
  eyeHeightPreset: EyeHeightPreset;
  eyeHeightM: number;
  /** GPS accuracy threshold before the overlay is shown (m). */
  gpsThresholdM: number;
  /** Global overlay opacity factor 0.2–1. */
  overlayOpacity: number;
  /** View distance (m): overlay layers fade out between 0.7x this and this, and aren't drawn beyond it. */
  viewDistanceM: number;
  declinationMode: 'auto' | 'manual';
  declinationDeg: number;
  trackAlignment: boolean;
  showMinimap: boolean;
}

export interface DebugSettings {
  enabled: boolean;
  mockGps: boolean;
  mockLat: number;
  mockLon: number;
  mockAccuracyM: number;
  /** Mock movement: the mock position moves at this speed along the current heading (0 = stationary). */
  mockSpeedKmh: number;
  mockHeading: boolean;
  mockHeadingDeg: number;
  showRaw: boolean;
  showHorizon: boolean;
  /** Vertical FOV used in sim mode (portrait), deg. */
  cameraVFovDeg: number;
}

export interface Store<T> {
  get(): T;
  update(patch: Partial<T>): void;
  subscribe(cb: (value: T) => void): () => void;
}

// ─── HUD / UI ───────────────────────────────────────────────────────────────

export interface HudMessage {
  kind: 'info' | 'warn' | 'error';
  text: string;
  /** Optional action button; clicking calls UiCallbacks.onAction(id). */
  action?: { id: string; label: string };
}

export interface HudState {
  mode: RenderMode;
  headingDeg: number | null;
  gpsAccuracy: number | null;
  gpsGate: GpsGate;
  gpsThresholdM: number;
  tracking: TrackingState;
  zone: ZoneStatus | null;
  speedKmh: number | null;
  /** Rotation (deg, 0/90/180/270) the HUD root must apply so it is upright for the user (AR orientation lock). */
  hudRotationDeg: number;
  /** e.g. "Kein See in der Nähe", "Lade Seedaten…" */
  lakeMessage: string | null;
  message: HudMessage | null;
  /** Key/value rows shown in the debug overlay (only when debug.showRaw). */
  debugRows: [string, string][] | null;
}

export interface UiCallbacks {
  onStart(): void;
  onExit(): void;
  onAction(id: string): void;
  /** Debug: move the mock position by metres (east, north). */
  onMockNudge(dEast: number, dNorth: number): void;
  /** Minimap tapped: open the fullscreen 2D map. */
  onOpenMap(): void;
}

// ─── 2D map (minimap + fullscreen map) ──────────────────────────────────────

export interface MapView {
  /** Root element to mount into the HUD. */
  readonly element: HTMLElement;
  setLakes(lakes: LakeFile[]): void;
  setUser(p: LatLon | null, headingDeg: number | null, accuracyM: number | null): void;
  /** 'mini' = small, non-interactive, follows the user; 'full' = fullscreen, pan/pinch. */
  setMode(mode: 'mini' | 'full' | 'hidden'): void;
  /** CSS rotation (deg) applied to an ancestor; pointer input must be corrected by it. */
  setInputRotation(deg: number): void;
  onRequestFull(cb: () => void): () => void;
  onClose(cb: () => void): () => void;
  destroy(): void;
}
